import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { useTranslation } from "react-i18next";
import { Save, Plus, Trash2, X } from "lucide-react";

const LANGS = ["fr", "en", "de", "zh-CN"];

function I18nInput({ value, onChange, label, textarea = false }) {
  const v = value || {};
  return (
    <div>
      <label className="overline mb-2 block">{label}</label>
      <div className="space-y-2">
        {LANGS.map(l => (
          <div key={l} className="flex gap-2 items-start">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#D4AF37] w-12 pt-3">{l}</span>
            {textarea ? (
              <textarea rows={2} value={v[l] || ""} onChange={e => onChange({ ...v, [l]: e.target.value })} className="input-luxe flex-1" />
            ) : (
              <input value={v[l] || ""} onChange={e => onChange({ ...v, [l]: e.target.value })} className="input-luxe flex-1" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminMenu() {
  const { t } = useTranslation();
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [activeCat, setActiveCat] = useState("");

  const load = async () => {
    const { data } = await api.get("/menu/all");
    setCats(data.categories);
    setItems(data.items);
    if (!activeCat && data.categories[0]) setActiveCat(data.categories[0].key);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const newItem = () => setEditing({ category: activeCat, name: { fr: "" }, description: { fr: "" }, price: 0, tags: [], available: true, image_url: "" });

  const save = async () => {
    if (editing.id) {
      await api.put(`/menu/items/${editing.id}`, editing);
    } else {
      await api.post("/menu/items", editing);
    }
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cet élément ?")) return;
    await api.delete(`/menu/items/${id}`);
    load();
  };

  const toggleTag = (tag) => setEditing(e => ({ ...e, tags: (e.tags || []).includes(tag) ? e.tags.filter(t => t !== tag) : [...(e.tags || []), tag] }));

  const visible = items.filter(i => i.category === activeCat);

  return (
    <div className="p-8 lg:p-12" data-testid="admin-menu-page">
      <div className="overline">Back-office</div>
      <h1 className="font-serif-luxe text-4xl text-[#F8F5EF] mt-2 mb-8">{t("admin.menu")}</h1>

      <div className="flex flex-wrap gap-2 mb-6">
        {cats.map(c => (
          <button key={c.key} onClick={() => setActiveCat(c.key)} data-testid={`admin-cat-${c.key}`}
            className={`px-4 py-2 text-[10px] tracking-[0.2em] uppercase border ${activeCat === c.key ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-[#D4AF37]/20 text-[#F8F5EF]/70"}`}>
            {c.name?.fr}
          </button>
        ))}
      </div>

      <button onClick={newItem} data-testid="admin-new-item" className="btn-gold mb-6"><Plus size={14} /> Nouveau plat</button>

      <div className="luxe-card divide-y divide-[#D4AF37]/10">
        {visible.length === 0 && <div className="p-8 text-center text-[#F8F5EF]/50">—</div>}
        {visible.map(it => (
          <div key={it.id} className="p-4 flex items-center gap-4" data-testid={`admin-item-${it.id}`}>
            <div className="flex-1">
              <div className="font-serif-luxe text-lg text-[#F8F5EF]">{it.name?.fr}</div>
              <div className="text-xs text-[#F8F5EF]/60 line-clamp-1">{it.description?.fr}</div>
            </div>
            <div className="text-[#D4AF37] font-serif-luxe text-lg">{it.price.toFixed(2)} €</div>
            <button onClick={() => setEditing(it)} className="btn-ghost-gold text-[10px]">Modifier</button>
            <button onClick={() => remove(it.id)} className="text-red-400 hover:text-red-300" data-testid={`admin-delete-${it.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="admin-editor">
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 relative">
            <button onClick={() => setEditing(null)} className="absolute top-4 right-4 text-[#D4AF37]" data-testid="admin-editor-close"><X size={20} /></button>
            <div className="overline mb-4">{editing.id ? "Modifier" : "Nouveau"}</div>
            <div className="space-y-5">
              <div>
                <label className="overline mb-2 block">Catégorie</label>
                <select value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} className="input-luxe">
                  {cats.map(c => <option key={c.key} value={c.key} className="bg-[#1A1A1A]">{c.name?.fr}</option>)}
                </select>
              </div>
              <I18nInput label="Nom" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              <I18nInput label="Description" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} textarea />
              <div>
                <label className="overline mb-2 block">Prix (€)</label>
                <input type="number" step="0.01" value={editing.price} onChange={e => setEditing({ ...editing, price: parseFloat(e.target.value) })} className="input-luxe" />
              </div>
              <div>
                <label className="overline mb-2 block">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {["signature", "vegetarian", "halal", "spicy", "gluten_free"].map(tag => (
                    <button key={tag} type="button" onClick={() => toggleTag(tag)}
                      className={`px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase border ${(editing.tags || []).includes(tag) ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-[#D4AF37]/20 text-[#F8F5EF]/70"}`}>
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.available} onChange={e => setEditing({ ...editing, available: e.target.checked })} />
                <span>Disponible</span>
              </label>
              <button onClick={save} className="btn-gold w-full justify-center" data-testid="admin-save-item"><Save size={14} /> Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
